import { authService } from '../services/authService';
import { isWelcomeCompleted } from '../utils/welcomeSession';

const DISMISS_KEY = 'welcome_resume_dismissed';

/**
 * Decide se deve oferecer o "retomar assistente" para uma conta que caiu no
 * Admin com isNewUser=false mas está completamente vazia.
 *
 * Contexto: o gate do App.jsx só mostra o WelcomeWizard quando o JWT traz
 * isNewUser=true. Essa claim só existe no 1º login (quando a linha em `users`
 * é criada). Qualquer 2º login reautentica com isNewUser=false e a pessoa
 * desaba no Admin normal sem nunca ter completado o wizard — perdendo a
 * criação automática das páginas públicas.
 *
 * Predicado ESTRITO (anti-duplicidade): só elegível quando NÃO existe nenhuma
 * entidade de configuração. Assim que a pessoa cria qualquer coisa (1 serviço
 * que seja), deixa de ser elegível — não sequestramos quem já começou a montar
 * manualmente. Rodar o Welcome por cima duplicaria tudo, já que a idempotência
 * do próprio Welcome é place-only (checa só existingPlaces.Any()).
 *
 * Custo: zero de backend — opera sobre os stats que o DashboardPage já carrega.
 *
 * @param {{ stats: object, loading: boolean }} params
 * @returns {{ eligible: boolean, dismiss: () => void }}
 */
export function useWelcomeResumePrompt({ stats, loading }) {
  const isEmpty =
    (stats?.professionals ?? 0) === 0 &&
    (stats?.services ?? 0) === 0 &&
    (stats?.places ?? 0) === 0 &&
    (stats?.schedules ?? 0) === 0;

  let dismissed = false;
  try {
    dismissed = sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    dismissed = false;
  }

  const eligible =
    !loading &&
    !!stats &&
    authService.isNewUser() === false &&
    !isWelcomeCompleted() &&
    isEmpty &&
    !dismissed;

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* sessionStorage indisponível — dismiss vira apenas a sessão atual do componente */
    }
  };

  return { eligible, dismiss };
}
